export const MARKY_WEBSITE_LAST_REVIEWED = '2026-08-30';

export type MarkyWebsiteAccent =
  | 'olive'
  | 'brick'
  | 'forest'
  | 'cobalt'
  | 'terracotta'
  | 'teal';

export interface MarkyWebsiteFaq {
  question: string;
  answer: string;
}

export interface MarkyWebsiteUseCase {
  slug: string;
  name: string;
  shortName: string;
  eyebrow: string;
  seoTitle: string;
  metaDescription: string;
  primaryKeyword: string;
  heroTitle: string;
  heroDescription: string;
  definition: string;
  cardDescription: string;
  image: string;
  imageAlt: string;
  accent: MarkyWebsiteAccent;
  features: Array<{
    title: string;
    description: string;
  }>;
  dataModel: Array<{
    entity: string;
    purpose: string;
  }>;
  buildSteps: Array<{
    title: string;
    description: string;
  }>;
  buildBrief: string;
  faqs: MarkyWebsiteFaq[];
  relatedSlugs: string[];
}

export const MARKY_WEBSITE_FAQS: MarkyWebsiteFaq[] = [
  {
    question: 'Can Marky build a full-stack website, not just a landing page?',
    answer:
      'Yes. Marky can start with a lightweight React website or create a full-stack web app with an Express backend, typed APIs, a Neon Postgres database, Drizzle models, authentication, file storage, and server-side integrations. The scaffold is chosen from the requirements in your brief.',
  },
  {
    question: 'Can a Marky website include Google login?',
    answer:
      'Yes. Full-stack projects include an authentication foundation and support a centralized Google OAuth flow. Marky can build the sign-in screens and authenticated product experience, then test the flow in preview before the app is published.',
  },
  {
    question: 'Where are Marky websites hosted?',
    answer:
      'The built-in production publishing flow deploys websites and web apps to Google Cloud Run. Every published project can use an editable easy-peasy.site subdomain, connect an existing custom domain, or purchase a new domain through the project settings.',
  },
  {
    question: 'Can Marky deploy a website to AWS?',
    answer:
      "Google Cloud Run is Marky's built-in deployment target. Because the generated project is a real TypeScript and Node.js codebase that can be pushed to GitHub, a developer can adapt it for AWS. AWS services and credentials are an integration path, not the default one-click publish flow.",
  },
  {
    question: 'Do I own or export the website code?',
    answer:
      'You can connect GitHub, create a new repository or link an existing one, and push the project code and later changes. Marky also keeps preview checkpoints so you can inspect versions and roll the editable project—or an eligible live deployment—back to an earlier state.',
  },
  {
    question: 'Can I add a database after starting with a simple website?',
    answer:
      'Yes. Marky can upgrade a static web project to the full-stack scaffold when the site grows to need user accounts, stored records, backend APIs, uploads, or AI features. The upgrade provisions the database and authentication foundation without requiring a brand-new conversation.',
  },
];

export const markyWebsiteUseCases: MarkyWebsiteUseCase[] = [
  {
    slug: 'ai-saas-app-builder',
    name: 'AI SaaS app builder',
    shortName: 'SaaS apps',
    eyebrow: 'For founders and product teams',
    seoTitle: 'AI SaaS App Builder with Database',
    metaDescription:
      'Build a SaaS web app from chat with React, Neon Postgres, Google login, backend APIs, live preview, GitHub export, and Cloud Run deployment.',
    primaryKeyword: 'AI SaaS app builder with database and auth',
    heroTitle: 'Build a SaaS app with AI, database and login included',
    heroDescription:
      'Describe the product, users and core workflow. Marky turns the brief into an editable React application with a real backend, persistent data, account flows and a production path.',
    definition:
      'An AI SaaS app builder turns a product brief into working software rather than a static mockup. Marky can create the React interface, backend APIs, Postgres schema, authentication and deployment configuration in one project, then refine the product through the same conversation.',
    cardDescription:
      'Multi-user products with accounts, dashboards, APIs and persistent application data.',
    image: '/images/marky-websites/ai-saas-app-builder.webp',
    imageAlt:
      'Illustrative SaaS analytics app with customer metrics, activity chart and data table',
    accent: 'olive',
    features: [
      {
        title: 'Accounts and onboarding',
        description:
          'Create registration, Google sign-in, first-run setup and authenticated routes around your product flow.',
      },
      {
        title: 'Product data that persists',
        description:
          'Model users, workspaces and core records in Neon Postgres with typed Drizzle queries and migrations.',
      },
      {
        title: 'Backend APIs and business rules',
        description:
          'Keep permissions, calculations, webhooks and private API calls in the Express and tRPC server layer.',
      },
      {
        title: 'Operator and customer views',
        description:
          'Build the customer product alongside practical admin screens for support, content or operations.',
      },
      {
        title: 'AI-native product features',
        description:
          'Add text, chat, image, video, speech or transcription features through the Easy-Peasy.AI API when the brief needs them.',
      },
      {
        title: 'A real launch path',
        description:
          'Test a public preview, save a version, publish to Cloud Run and connect a domain when you approve the result.',
      },
    ],
    dataModel: [
      {
        entity: 'Users',
        purpose: 'Profiles, authentication identity and access state',
      },
      {
        entity: 'Workspaces',
        purpose: 'Teams, ownership and multi-tenant product boundaries',
      },
      {
        entity: 'Product records',
        purpose: 'The projects, documents, jobs or assets your SaaS manages',
      },
      {
        entity: 'Plans and events',
        purpose: 'Entitlements, usage history and billing integration hooks',
      },
    ],
    buildSteps: [
      {
        title: 'Define the smallest valuable workflow',
        description:
          'Name the user, the problem and the one outcome the first release must complete.',
      },
      {
        title: 'Model users and product data',
        description:
          'Marky drafts the database schema and API contracts before building the screens around them.',
      },
      {
        title: 'Build and test the product loop',
        description:
          'The agent implements the UI, server logic and tests, then opens a live preview for review.',
      },
      {
        title: 'Checkpoint, publish and keep iterating',
        description:
          'Approve a version for Cloud Run, connect GitHub or a domain, and request the next feature in chat.',
      },
    ],
    buildBrief:
      'Build a SaaS app for research teams. Users should sign in with Google, create a workspace, upload interview notes, tag findings and see an evidence dashboard. Add an admin view, a Neon Postgres schema, secure server APIs and a responsive onboarding flow.',
    faqs: [
      {
        question: 'Can Marky build a SaaS backend as well as the interface?',
        answer:
          'Yes. The full-stack scaffold includes an Express server, typed tRPC APIs, Drizzle database access and a managed Neon Postgres branch. Marky can implement server-side business rules instead of placing sensitive logic in the browser.',
      },
      {
        question: 'Can the SaaS app use Google sign-in?',
        answer:
          "Yes. The scaffold supports Google OAuth through Marky's centralized authentication proxy, alongside the project's Better Auth foundation. The exact account and onboarding experience is generated for the product you describe.",
      },
      {
        question: 'Can I add Stripe subscriptions?',
        answer:
          'Marky can build a Stripe integration when you provide the required Stripe credentials through project secrets. Payments are an external integration that should be tested in Stripe test mode before accepting real transactions.',
      },
      {
        question: 'Can I export the SaaS code?',
        answer:
          'Yes. Connect GitHub from project settings, create or link a repository, and push the current checkpoint. The generated application uses standard TypeScript, React and Node.js project files rather than a proprietary page format.',
      },
    ],
    relatedSlugs: [
      'ai-membership-website-builder',
      'ai-client-portal-builder',
      'ai-internal-tool-builder',
    ],
  },
  {
    slug: 'ai-client-portal-builder',
    name: 'AI client portal builder',
    shortName: 'Client portals',
    eyebrow: 'For agencies and service teams',
    seoTitle: 'AI Client Portal Builder with Login',
    metaDescription:
      'Build a branded client portal with secure login, project status, approvals, file uploads, a Postgres database, custom domain, and editable code.',
    primaryKeyword: 'AI client portal builder with login and file uploads',
    heroTitle: 'Build a client portal around the way your team actually works',
    heroDescription:
      'Replace scattered status emails and shared folders with a branded portal for projects, files, approvals and next steps—shaped around your own service workflow.',
    definition:
      'An AI client portal builder creates a private web application where each client can sign in and interact with the records meant for them. Marky can build the client experience, internal operator view, database, file uploads and approval workflow as one editable full-stack project.',
    cardDescription:
      'Branded spaces for projects, files, approvals, invoices and client-specific updates.',
    image: '/images/marky-websites/ai-client-portal-builder.webp',
    imageAlt:
      'Illustrative client portal with milestones, deliverable approvals and shared files',
    accent: 'brick',
    features: [
      {
        title: 'Private client workspaces',
        description:
          'Give authenticated clients a focused view of their projects, milestones and requested actions.',
      },
      {
        title: 'Files tied to real records',
        description:
          'Store uploads in S3-compatible object storage and associate each asset with the right client or deliverable.',
      },
      {
        title: 'Review and approval flows',
        description:
          'Capture feedback, approval state, timestamps and revision history instead of losing decisions in email.',
      },
      {
        title: 'Internal operations view',
        description:
          'Keep private notes and team controls separate from the simplified experience your clients see.',
      },
      {
        title: 'Your services and integrations',
        description:
          'Connect payment, email, CRM or document APIs through protected project secrets when needed.',
      },
      {
        title: 'Branded URL and source code',
        description:
          'Publish on an editable subdomain or custom domain, then sync the project to your GitHub repository.',
      },
    ],
    dataModel: [
      {
        entity: 'Clients',
        purpose: 'Organizations, contacts and access scope',
      },
      {
        entity: 'Projects',
        purpose: 'Status, owners, milestones and client-visible progress',
      },
      {
        entity: 'Deliverables',
        purpose: 'Files, versions, feedback and approval decisions',
      },
      {
        entity: 'Activity',
        purpose: 'Comments, requests, notifications and audit history',
      },
    ],
    buildSteps: [
      {
        title: 'Map what clients should see',
        description:
          'Separate client-visible records and actions from private notes, controls and team workflows.',
      },
      {
        title: 'Design the access and data rules',
        description:
          'Define clients, projects, files and approvals before Marky implements the authenticated views.',
      },
      {
        title: 'Test both sides of the portal',
        description:
          'Review the customer experience and the internal operator path in the live preview.',
      },
      {
        title: 'Launch under your brand',
        description:
          'Save the approved version, publish it, connect a custom domain and keep the code in GitHub.',
      },
    ],
    buildBrief:
      'Build a client portal for a branding studio. Clients sign in with Google, see project milestones, review two deliverable types, upload reference files and approve work with comments. Staff need a private dashboard for deadlines, revisions and client requests.',
    faqs: [
      {
        question: 'Can different clients see different portal data?',
        answer:
          'Yes, when that rule is included in the brief and implemented in the server and database access layer. Describe the roles and boundaries clearly so Marky can build and test client-scoped queries rather than relying on visual hiding alone.',
      },
      {
        question: 'Can clients upload and download files?',
        answer:
          'Yes. Full-stack projects can use the included S3-compatible object storage configuration for uploads. Marky can connect file metadata to database records so a document belongs to the correct project, deliverable and client.',
      },
      {
        question: 'Can the portal collect payments or show invoices?',
        answer:
          'Marky can create invoice records and integrate a payment provider such as Stripe when you add the provider credentials. Payment behavior and webhook handling should be specified and tested as a dedicated part of the build.',
      },
      {
        question: 'Is a client portal suitable for sensitive regulated data?',
        answer:
          'A generated portal should receive a dedicated security and compliance review before handling regulated or highly sensitive information. Marky can implement the application, but your legal, privacy and security requirements still determine the production controls you need.',
      },
    ],
    relatedSlugs: [
      'ai-internal-tool-builder',
      'ai-saas-app-builder',
      'ai-membership-website-builder',
    ],
  },
  {
    slug: 'ai-booking-website-builder',
    name: 'AI booking website builder',
    shortName: 'Booking websites',
    eyebrow: 'For studios and service businesses',
    seoTitle: 'AI Booking Website Builder',
    metaDescription:
      'Build a custom booking website for services, staff and availability with a Postgres database, admin view, integrations, custom domain, and real code.',
    primaryKeyword: 'AI booking website builder for service businesses',
    heroTitle: 'Build a booking website that fits your actual schedule',
    heroDescription:
      'Create a tailored reservation flow for a salon, studio, consultant, class, tour or appointment-based business—without forcing the service into a generic template.',
    definition:
      'An AI booking website builder creates the public service pages and the scheduling workflow behind them. Marky can model services, staff, availability and booking requests in Postgres, build customer and admin views, and connect a calendar or payment provider when you supply its API access.',
    cardDescription:
      'Service pages, staff availability, reservation records and an operator schedule.',
    image: '/images/marky-websites/ai-booking-website-builder.webp',
    imageAlt:
      'Illustrative wellness booking website with service details, dates and available appointment times',
    accent: 'forest',
    features: [
      {
        title: 'A service-first public website',
        description:
          'Explain offers, duration, location and pricing before guiding visitors into the right booking path.',
      },
      {
        title: 'Custom availability rules',
        description:
          'Represent staff, services, opening windows, buffers and booking status in the database schema.',
      },
      {
        title: 'Clear customer states',
        description:
          'Show whether a request is pending, confirmed, rescheduled or cancelled instead of implying a slot is secured too early.',
      },
      {
        title: 'An operator schedule',
        description:
          'Give staff a practical view for upcoming appointments, intake details and status changes.',
      },
      {
        title: 'Provider integrations',
        description:
          'Connect calendar, email, SMS or payment services through their APIs and protected environment variables.',
      },
      {
        title: 'Mobile booking and custom domain',
        description:
          'Design the conversion path for touch screens, preview it publicly and launch it on your business domain.',
      },
    ],
    dataModel: [
      { entity: 'Services', purpose: 'Duration, price, location and capacity' },
      { entity: 'Staff', purpose: 'Skills, schedules and service assignments' },
      {
        entity: 'Availability',
        purpose: 'Opening windows, exceptions, buffers and blocked time',
      },
      {
        entity: 'Bookings',
        purpose: 'Customer details, status, notes and external references',
      },
    ],
    buildSteps: [
      {
        title: 'Describe the booking truth',
        description:
          'List services, durations, staff, locations and what makes a reservation confirmed.',
      },
      {
        title: 'Choose native records or an integration',
        description:
          'Marky can build a custom request system or work with a scheduling provider that exposes an API.',
      },
      {
        title: 'Test the awkward cases',
        description:
          'Review double-booking prevention, time zones, cancellations, unavailable days and mobile input.',
      },
      {
        title: 'Publish the customer path',
        description:
          'Approve the preview, connect the domain and keep improving copy or operations through chat.',
      },
    ],
    buildBrief:
      'Build a booking website for a four-person wellness studio. Visitors choose a service, practitioner and available time, then submit contact details. Staff need a weekly schedule, blocked-time controls and pending/confirmed states. Make the mobile booking flow the priority.',
    faqs: [
      {
        question: 'Does Marky include a native scheduling service?',
        answer:
          'Marky can build a database-backed booking workflow as part of the generated app. If a separate scheduling system must be the source of truth, Marky can integrate it when the provider offers a suitable API, webhook or embed and you provide access.',
      },
      {
        question: 'Can the website sync with Google Calendar?',
        answer:
          'A Google Calendar integration can be added through the appropriate API and credentials. The booking rules should define which calendar is authoritative, how conflicts are handled and whether a request is pending until the external event is created.',
      },
      {
        question: 'Can customers pay a deposit?',
        answer:
          'Yes, Marky can integrate a payment service such as Stripe using project secrets. Deposit amount, refund rules, webhook events and the relationship between payment and booking confirmation should be included in the brief and tested.',
      },
      {
        question: 'Will the booking website work on phones?',
        answer:
          'Marky builds responsive React interfaces and can make the touch booking path a first-class requirement. The preview should still be tested on real mobile devices, especially date pickers, keyboards, long service names and time-zone messaging.',
      },
    ],
    relatedSlugs: [
      'ai-client-portal-builder',
      'ai-membership-website-builder',
      'ai-saas-app-builder',
    ],
  },
  {
    slug: 'ai-membership-website-builder',
    name: 'AI membership website builder',
    shortName: 'Membership sites',
    eyebrow: 'For creators and communities',
    seoTitle: 'AI Membership Website Builder',
    metaDescription:
      'Build a membership website with login, gated content, progress data, admin controls, optional Stripe billing, a custom domain, and editable source code.',
    primaryKeyword: 'AI membership website builder with member login',
    heroTitle: 'Build a membership website your content can grow into',
    heroDescription:
      'Turn an audience, curriculum or professional community into a branded member experience with login, access rules, content records and progress that persists.',
    definition:
      'An AI membership website builder combines a public acquisition site with a private member application. Marky can create sign-in, protected routes, content libraries, access tiers, progress tracking and administration on a real database, with optional billing added through a provider integration.',
    cardDescription:
      'Member login, gated libraries, tiers, progress and community-oriented dashboards.',
    image: '/images/marky-websites/ai-membership-website-builder.webp',
    imageAlt:
      'Illustrative membership learning site with course library, progress and live sessions',
    accent: 'cobalt',
    features: [
      {
        title: 'Public and private experiences',
        description:
          'Create the sales pages, account entry points and authenticated member home as one cohesive site.',
      },
      {
        title: 'Content access rules',
        description:
          'Model free, paid or cohort access in the database and enforce it in server-backed routes.',
      },
      {
        title: 'A useful member library',
        description:
          'Organize lessons, resources, recordings or downloads with search, status and saved progress.',
      },
      {
        title: 'Member and admin views',
        description:
          'Give members a focused experience while operators manage content, access and support records.',
      },
      {
        title: 'Optional subscriptions',
        description:
          'Connect Stripe or another provider with your credentials and map verified billing events to entitlements.',
      },
      {
        title: 'Room for AI experiences',
        description:
          'Add a member assistant, lesson summaries, images, voice or other AI features through secure server calls.',
      },
    ],
    dataModel: [
      { entity: 'Members', purpose: 'Identity, profile and account state' },
      {
        entity: 'Plans',
        purpose: 'Tiers, entitlements and billing references',
      },
      {
        entity: 'Content',
        purpose: 'Courses, lessons, resources and publish state',
      },
      {
        entity: 'Progress',
        purpose: 'Completion, bookmarks, attendance and activity',
      },
    ],
    buildSteps: [
      {
        title: 'Define the membership promise',
        description:
          'Clarify who joins, what they receive and which actions make the membership valuable.',
      },
      {
        title: 'Model content and entitlements',
        description:
          'Marky structures plans, access rules, lessons and progress before designing the member home.',
      },
      {
        title: 'Test access boundaries',
        description:
          'Preview logged-out, free, paid and admin paths so protected content is enforced beyond the UI.',
      },
      {
        title: 'Launch, learn and extend',
        description:
          'Publish the approved version, connect a domain and add community or billing features deliberately.',
      },
    ],
    buildBrief:
      'Build a membership site for an independent design history club. Include a public overview, Google sign-in, free and paid access levels, a searchable lesson library, progress tracking, live-session listings and an admin area for publishing content.',
    faqs: [
      {
        question: 'Can Marky protect members-only pages?',
        answer:
          'Yes. Marky can implement authenticated routes and server-side access checks based on membership records. The access rules should be explicit—such as free, paid, cohort or admin—so the database and API layer enforce the same boundaries as the interface.',
      },
      {
        question: 'Can the site track course or content progress?',
        answer:
          'Yes. Progress can be modeled as persistent records tied to a member and content item, including completion, bookmarks, attendance or last position. Marky can then surface that data in a member dashboard and operator reports.',
      },
      {
        question: 'Does membership billing come built in?',
        answer:
          'The scaffold provides the backend, database and secrets needed for an integration, but billing is added through a provider such as Stripe. Plans, trials, cancellations and webhook behavior should be specified and verified in test mode.',
      },
      {
        question: 'Can I start without paid memberships?',
        answer:
          'Yes. Start with authentication, a free member library or a manual access workflow, then add billing when the offer is validated. Marky can evolve the same full-stack project and keep checkpoints before larger changes.',
      },
    ],
    relatedSlugs: [
      'ai-saas-app-builder',
      'ai-client-portal-builder',
      'ai-booking-website-builder',
    ],
  },
  {
    slug: 'ai-directory-website-builder',
    name: 'AI directory website builder',
    shortName: 'Directory websites',
    eyebrow: 'For niche publishers and marketplaces',
    seoTitle: 'AI Directory Website Builder',
    metaDescription:
      'Build a searchable directory website with Postgres listings, categories, filters, submissions, moderation, maps, a custom domain, and editable React code.',
    primaryKeyword: 'AI directory website builder with database',
    heroTitle:
      'Build a directory website around your niche—not a rigid template',
    heroDescription:
      'Create searchable listings, category pages, submissions and moderation for local businesses, resources, vendors, properties, jobs or any structured collection.',
    definition:
      'An AI directory website builder creates both the public discovery experience and the data system behind it. Marky can model listings and categories in Postgres, build search and filters, add submission and moderation workflows, and integrate mapping or payments when the project requires them.',
    cardDescription:
      'Searchable listings, categories, submissions, moderation and map-ready data.',
    image: '/images/marky-websites/ai-directory-website-builder.webp',
    imageAlt:
      'Illustrative local makers directory with search, categories, listings and a map',
    accent: 'terracotta',
    features: [
      {
        title: 'Structured listing data',
        description:
          'Define the fields that make the niche useful instead of squeezing every listing into a generic profile.',
      },
      {
        title: 'Search, filters and categories',
        description:
          'Build discovery around location, type, attributes, availability or other meaningful facets.',
      },
      {
        title: 'Submission and moderation',
        description:
          'Let contributors submit or edit records while an operator reviews status and publication.',
      },
      {
        title: 'Indexable detail pages',
        description:
          'Give approved listings stable routes, descriptive metadata and internal links from category pages.',
      },
      {
        title: 'Maps and external data',
        description:
          'Connect a mapping, geocoding or enrichment provider when you add the relevant API credentials.',
      },
      {
        title: 'Monetization paths',
        description:
          'Add featured placements, subscriptions or lead flows through an explicitly designed payment integration.',
      },
    ],
    dataModel: [
      { entity: 'Listings', purpose: 'Core profile fields and publish state' },
      { entity: 'Categories', purpose: 'Taxonomy, filters and browse pages' },
      {
        entity: 'Locations',
        purpose: 'Addresses, coordinates and geographic relationships',
      },
      {
        entity: 'Submissions',
        purpose: 'Contributors, revisions, reviews and moderation history',
      },
    ],
    buildSteps: [
      {
        title: 'Choose a narrow directory promise',
        description:
          'Define who searches, what they compare and which listing data helps them decide.',
      },
      {
        title: 'Design the taxonomy first',
        description:
          'Marky models listing fields, categories, locations and moderation before building browse pages.',
      },
      {
        title: 'Build real discovery paths',
        description:
          'Implement filters, detail routes, submissions and operator review with realistic sample states.',
      },
      {
        title: 'Publish a useful first collection',
        description:
          'Launch on a domain after reviewing quality, then extend the schema as the niche teaches you more.',
      },
    ],
    buildBrief:
      'Build a directory for independent makers in one city. Visitors can search by craft and neighborhood, view map-ready profiles, bookmark listings and submit corrections. Makers can request a listing; an admin reviews every submission before it is published.',
    faqs: [
      {
        question: 'Can Marky create searchable listing pages?',
        answer:
          'Yes. Listings, categories and locations can live in Postgres and be exposed through typed backend APIs. Marky can build filterable browse pages and stable detail routes around the fields that matter to your directory niche.',
      },
      {
        question: 'Can users submit their own listings?',
        answer:
          'Yes. Marky can create submission forms, contributor accounts and moderation states such as draft, pending, approved and rejected. The operator view can keep unpublished data separate until a listing passes review.',
      },
      {
        question: 'Can the directory include a map?',
        answer:
          'Yes, through a mapping provider that offers the needed SDK or API. You supply the provider key through project secrets; Marky can store coordinates and connect map markers to the same listing records used by search.',
      },
      {
        question: 'Can directory pages be optimized for search?',
        answer:
          'Marky can implement crawlable routes, titles, descriptions, structured data, sitemaps and internal linking when requested. Useful, unique listing data is still essential—mass-producing empty location or category pages would create thin content rather than durable SEO value.',
      },
    ],
    relatedSlugs: [
      'ai-internal-tool-builder',
      'ai-booking-website-builder',
      'ai-saas-app-builder',
    ],
  },
  {
    slug: 'ai-internal-tool-builder',
    name: 'AI internal tool builder',
    shortName: 'Internal tools',
    eyebrow: 'For operations and support teams',
    seoTitle: 'AI Internal Tool Builder with Database',
    metaDescription:
      'Build a custom internal tool with Postgres records, CRUD workflows, filters, role-aware views, secure integrations, GitHub export, and Cloud Run hosting.',
    primaryKeyword: 'AI internal tool builder with database',
    heroTitle: 'Build the internal tool your spreadsheet is trying to become',
    heroDescription:
      'Turn inventory, approvals, intake, support or operational records into a purpose-built web app with real data, clear states and the controls your team needs.',
    definition:
      'An AI internal tool builder turns an operational workflow into a database-backed application for a specific team. Marky can create the schema, CRUD screens, filters, dashboards, server actions and integrations, then publish the tool or push its source code to GitHub for continued development.',
    cardDescription:
      'CRUD workflows, inventory, approvals, reports and team-specific operations screens.',
    image: '/images/marky-websites/ai-internal-tool-builder.webp',
    imageAlt:
      'Illustrative inventory operations tool with filters, warehouse summaries and status table',
    accent: 'teal',
    features: [
      {
        title: 'A schema for the real workflow',
        description:
          'Turn spreadsheet columns, handoffs and statuses into relational records with clear ownership.',
      },
      {
        title: 'Practical CRUD interfaces',
        description:
          'Create, review, update and archive records through forms, tables, filters and focused detail views.',
      },
      {
        title: 'Role-aware workspaces',
        description:
          'Design different views and actions for operators, managers, reviewers or external participants.',
      },
      {
        title: 'Reports grounded in source data',
        description:
          'Summarize operational records with real queries instead of decorative dashboard metrics.',
      },
      {
        title: 'Secure service connections',
        description:
          'Use secrets for APIs such as email, SMS, AI, payments or cloud services without exposing keys in the client.',
      },
      {
        title: 'Versioned, portable code',
        description:
          'Preview each change, roll back checkpoints, publish to Cloud Run and sync the repository to GitHub.',
      },
    ],
    dataModel: [
      {
        entity: 'Records',
        purpose: 'The orders, cases, assets or requests being managed',
      },
      {
        entity: 'States',
        purpose: 'Workflow status, transitions and validation rules',
      },
      { entity: 'Assignments', purpose: 'Owners, teams, queues and due dates' },
      {
        entity: 'Activity log',
        purpose: 'Changes, comments, imports and integration events',
      },
    ],
    buildSteps: [
      {
        title: 'Start with the current process',
        description:
          'Describe the spreadsheet, inbox or manual handoff and the failure it creates for the team.',
      },
      {
        title: 'Model records and state changes',
        description:
          'Marky converts fields, permissions and transitions into a Postgres schema and typed APIs.',
      },
      {
        title: 'Test with realistic edge cases',
        description:
          'Check empty states, invalid transitions, duplicate records, long values and integration failures.',
      },
      {
        title: 'Roll out deliberately',
        description:
          'Approve the preview, publish it, add secrets and keep a checkpoint before every larger workflow change.',
      },
    ],
    buildBrief:
      'Build an inventory operations tool for a specialty retailer. Track items, warehouses, suppliers, stock movements and purchase orders. Add low-stock filters, receive-order actions, manager and operator views, CSV import, an audit trail and practical empty/error states.',
    faqs: [
      {
        question: 'Can Marky replace a spreadsheet workflow?',
        answer:
          'Often, yes—when the spreadsheet represents structured records and repeatable actions. Marky can model those records in Postgres and build validated forms, tables, filters and status changes. The original process still needs to be described accurately before it is automated.',
      },
      {
        question: 'Can an internal tool connect to our existing services?',
        answer:
          'Marky can integrate services that expose suitable APIs, webhooks or database connections. Credentials belong in project secrets, and each integration should include failure handling, permission boundaries and a clear source of truth.',
      },
      {
        question: 'Can different team roles have different actions?',
        answer:
          'Yes. Define the roles and allowed transitions in the brief so Marky can enforce them in server-side actions as well as in the interface. Hiding a button alone is not a sufficient permission control.',
      },
      {
        question: 'How do we recover if a change breaks the tool?',
        answer:
          'Successful previews create version checkpoints, and Marky can also save named checkpoints before risky changes. The project can be rolled back to an earlier working version; eligible published versions can also be restored to the live site.',
      },
    ],
    relatedSlugs: [
      'ai-client-portal-builder',
      'ai-directory-website-builder',
      'ai-saas-app-builder',
    ],
  },
];

export const getMarkyWebsiteUseCase = (
  slug: string,
): MarkyWebsiteUseCase | undefined =>
  markyWebsiteUseCases.find((useCase) => useCase.slug === slug);
